export type GstConfigLike = {
  gstPercentByUsage?: Record<string, number | string | null | undefined> | null;
  gstPercentByUnitType?: Record<string, number | string | null | undefined> | null;
  gstDefaultPercent?: number | string | null;
};

export type UnitLikeForGst = {
  blockType?: string | null;
  unitType?: string | null;
  type?: string | null;
  usage?: string | null;
  customFields?: Record<string, any> | null;
};

function normalizeTypeKey(value: any): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

export function resolveUnitTypeForGst(unit: UnitLikeForGst | null | undefined): string {
  const candidates = [
    (unit as any)?.usage,
    (unit as any)?.customFields?.usage,
    (unit as any)?.customFields?.USAGE,
    (unit as any)?.blockType,
    (unit as any)?.unitType,
    (unit as any)?.type,
    (unit as any)?.customFields?.block_type,
    (unit as any)?.customFields?.unit_type,
  ];
  for (const raw of candidates) {
    const value = String(raw || '').trim();
    if (value) return value;
  }
  return '';
}

export function resolveGstPercentForUnit(
  unit: UnitLikeForGst | null | undefined,
  config: GstConfigLike | null | undefined,
  fallbackPercent = 1
): number {
  const safeFallback = Number.isFinite(Number(fallbackPercent)) ? Number(fallbackPercent) : 1;
  const unitType = resolveUnitTypeForGst(unit);
  const byUsage = (config?.gstPercentByUsage || {}) as Record<string, any>;
  const byType = (config?.gstPercentByUnitType || {}) as Record<string, any>;
  const normalizedLookup = new Map<string, number>();
  Object.keys(byUsage || {}).forEach((rawKey) => {
    const key = normalizeTypeKey(rawKey);
    const value = Number(byUsage[rawKey]);
    if (!key) return;
    if (!Number.isFinite(value) || value < 0) return;
    normalizedLookup.set(key, value);
  });
  Object.keys(byType || {}).forEach((rawKey) => {
    const key = normalizeTypeKey(rawKey);
    const value = Number(byType[rawKey]);
    if (!key) return;
    if (!Number.isFinite(value) || value < 0) return;
    normalizedLookup.set(key, value);
  });
  const mapped = Number(unitType ? normalizedLookup.get(normalizeTypeKey(unitType)) : NaN);
  if (Number.isFinite(mapped) && mapped >= 0) return mapped;
  const defaultPercent = Number(config?.gstDefaultPercent);
  if (Number.isFinite(defaultPercent) && defaultPercent >= 0) return defaultPercent;
  return safeFallback;
}

export function computeGstAmountForUnit(
  unitAmount: number | null | undefined,
  unit: UnitLikeForGst | null | undefined,
  config: GstConfigLike | null | undefined,
  fallbackPercent = 1
): number {
  const amount = Number(unitAmount || 0);
  if (!(amount > 0)) return 0;
  const percent = resolveGstPercentForUnit(unit, config, fallbackPercent);
  return Math.ceil((amount * percent) / 100);
}
